import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import './index.css';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#2a52be',      // cerulean blue from techjusticelab.org
      dark: '#00075e',      // dark blue
      light: '#5578d4',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#00075e',      // dark blue
      contrastText: '#ffffff',
    },
    background: {
      default: '#f4f2ec',   // apple white from techjusticelab.org
      paper: '#ffffff',
    },
    text: {
      primary: '#00075e',   // dark blue for primary text
      secondary: '#838996', // roman silver for secondary text
    },
    divider: '#e0ddd6',
  },
  typography: {
    fontFamily: 'Georgia, "Times New Roman", Times, serif',
    h1: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 700 },
    h2: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 700 },
    h3: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 700 },
    h4: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 700 },
    h5: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 600 },
    h6: { fontFamily: 'Georgia, "Times New Roman", Times, serif', fontWeight: 600 },
    button: {
      fontFamily: '"Helvetica Neue", Arial, sans-serif',
      fontWeight: 500,
      textTransform: 'none',
      letterSpacing: '0.01em',
    },
    body1: { fontFamily: 'Georgia, "Times New Roman", Times, serif' },
    body2: { fontFamily: '"Helvetica Neue", Arial, sans-serif' },
    caption: { fontFamily: '"Helvetica Neue", Arial, sans-serif' },
    overline: { fontFamily: '"Helvetica Neue", Arial, sans-serif' },
  },
  shape: {
    borderRadius: 8,
  },
  shadows: [
    'none',
    '0 1px 3px rgba(0,7,94,0.06), 0 1px 2px rgba(0,7,94,0.04)',
    '0 3px 6px rgba(0,7,94,0.08), 0 2px 4px rgba(0,7,94,0.06)',
    '0 6px 12px rgba(0,7,94,0.10), 0 3px 6px rgba(0,7,94,0.07)',
    '0 10px 20px rgba(0,7,94,0.12), 0 4px 8px rgba(0,7,94,0.08)',
    '0 14px 28px rgba(0,7,94,0.14), 0 5px 10px rgba(0,7,94,0.09)',
    '0 18px 36px rgba(0,7,94,0.15), 0 6px 12px rgba(0,7,94,0.10)',
    '0 20px 40px rgba(0,7,94,0.15), 0 7px 14px rgba(0,7,94,0.10)',
    '0 22px 44px rgba(0,7,94,0.15), 0 8px 16px rgba(0,7,94,0.10)',
    '0 24px 48px rgba(0,7,94,0.15), 0 9px 18px rgba(0,7,94,0.10)',
    '0 26px 52px rgba(0,7,94,0.15), 0 10px 20px rgba(0,7,94,0.10)',
    '0 28px 56px rgba(0,7,94,0.15), 0 11px 22px rgba(0,7,94,0.10)',
    '0 30px 60px rgba(0,7,94,0.15), 0 12px 24px rgba(0,7,94,0.10)',
    '0 32px 64px rgba(0,7,94,0.15), 0 13px 26px rgba(0,7,94,0.10)',
    '0 34px 68px rgba(0,7,94,0.15), 0 14px 28px rgba(0,7,94,0.10)',
    '0 36px 72px rgba(0,7,94,0.15), 0 15px 30px rgba(0,7,94,0.10)',
    '0 38px 76px rgba(0,7,94,0.15), 0 16px 32px rgba(0,7,94,0.10)',
    '0 40px 80px rgba(0,7,94,0.15), 0 17px 34px rgba(0,7,94,0.10)',
    '0 42px 84px rgba(0,7,94,0.15), 0 18px 36px rgba(0,7,94,0.10)',
    '0 44px 88px rgba(0,7,94,0.15), 0 19px 38px rgba(0,7,94,0.10)',
    '0 46px 92px rgba(0,7,94,0.15), 0 20px 40px rgba(0,7,94,0.10)',
    '0 48px 96px rgba(0,7,94,0.15), 0 21px 42px rgba(0,7,94,0.10)',
    '0 50px 100px rgba(0,7,94,0.15), 0 22px 44px rgba(0,7,94,0.10)',
    '0 52px 104px rgba(0,7,94,0.15), 0 23px 46px rgba(0,7,94,0.10)',
    '0 54px 108px rgba(0,7,94,0.15), 0 24px 48px rgba(0,7,94,0.10)',
  ],
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          padding: '8px 20px',
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          letterSpacing: '0.01em',
          transition: 'all 0.2s ease',
        },
        contained: {
          boxShadow: '0 2px 6px rgba(42,82,190,0.25)',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(42,82,190,0.35)',
            transform: 'translateY(-1px)',
          },
        },
        outlined: {
          borderColor: '#2a52be',
          '&:hover': {
            backgroundColor: 'rgba(42,82,190,0.04)',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        elevation1: {
          boxShadow: '0 1px 4px rgba(0,7,94,0.08), 0 1px 2px rgba(0,7,94,0.05)',
        },
        elevation2: {
          boxShadow: '0 3px 8px rgba(0,7,94,0.10), 0 2px 4px rgba(0,7,94,0.06)',
        },
        elevation3: {
          boxShadow: '0 6px 16px rgba(0,7,94,0.12), 0 3px 6px rgba(0,7,94,0.08)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 2px 8px rgba(0,7,94,0.08), 0 1px 3px rgba(0,7,94,0.05)',
          transition: 'box-shadow 0.2s ease, transform 0.2s ease',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(0,7,94,0.12), 0 3px 8px rgba(0,7,94,0.08)',
          },
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 8,
            fontFamily: 'Georgia, "Times New Roman", Times, serif',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#2a52be',
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          fontWeight: 500,
          fontSize: '0.75rem',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            fontFamily: '"Helvetica Neue", Arial, sans-serif',
            fontWeight: 600,
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            color: '#838996',
            backgroundColor: '#f4f2ec',
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'Georgia, "Times New Roman", Times, serif',
          borderColor: '#e0ddd6',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#00075e',
          boxShadow: '0 2px 8px rgba(0,7,94,0.2)',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: '#ffffff',
          borderRight: '1px solid #e0ddd6',
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: '#838996',
          minWidth: 40,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          fontWeight: 500,
          fontSize: '0.9rem',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: '"Helvetica Neue", Arial, sans-serif',
          fontWeight: 500,
          textTransform: 'none',
          fontSize: '0.9rem',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontFamily: 'Georgia, "Times New Roman", Times, serif',
          fontWeight: 700,
          color: '#00075e',
        },
      },
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <BrowserRouter>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </BrowserRouter>
  </React.StrictMode>
);
